import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Card, CardContent, makeStyles, Grid, Typography, Chip } from "@material-ui/core";
import {
	CrudOptions,
	AttachmentListing,
} from "frame/components";
import { isNotNullOrEmpty, createRowSelector, useRouter, isStaffUser } from "utils";
import { useCurrentRole } from "utils/current-user";
import * as fromRoutes from "../actions/types";

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3),
		},
	},
	card: {
		display: "flex",
		flexWrap: "wrap",
		position: "relative",
		marginBottom: theme.spacing(2),
	},
	container: {
		margin: theme.spacing(0.5),
	},
	content: {
		padding: theme.spacing(2),
		flexGrow: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			flexWrap: "wrap",
		},
		"&:last-child": {
			paddingBottom: theme.spacing(2),
		},
	},
	error: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
	success: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.white,
	},
	label: {
		padding: theme.spacing(0, 0, 0.5, 0),
		color: theme.palette.text.tertiary,
	},
	value: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		padding: theme.spacing(0, 0, 0.5, 0),
	},
}));

const EquipmentListing = ({
	equipment = [],
	isLoading = false,
	onEdit,
	onDelete
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const role = useCurrentRole();

	return (
		<>
			{
				equipment && equipment.length > 0 && !isLoading ?
					equipment.map((item, index) => {
						const onRowSelect = createRowSelector(() => {
							history.push(`${fromRoutes.EQUIPMENT_ROUTE}/${item.id}`);
						});
						return (
						<Card
							data-cy={`equipment-${index}`}
							className={clsx(classes.card)}
							key={index}
							onClick={onRowSelect}
							style={{ cursor: "pointer" }}
						>
							<CardContent className={classes.content}>
								<Grid
									container
									spacing={2}
									className={classes.container}
								>
									<Grid item lg={2} md={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
										Equipment Name
										</Typography>
										<Typography variant="h6" className={classes.value}>
											{item.equipment || "-"}
										</Typography>
									</Grid>
									<Grid item lg={2} md={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
										Type
										</Typography>
										<Typography variant="h6" className={classes.value}>
											{item.type || "-"}
										</Typography>
									</Grid>
									<Grid item lg={2} md={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
										Make
										</Typography>
										<Typography variant="h6" className={classes.value}>
											{item.make || "-"}
										</Typography>
									</Grid>
									<Grid item lg={2} md={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
										Model
										</Typography>
										<Typography variant="h6" className={classes.value}>
											{item.model || "-"}
										</Typography>
									</Grid>
									<Grid item lg={1} md={6} xs={12}>
										<Typography variant="body2" className={classes.label}>
										Status
										</Typography>
										<Chip
											className={
												item.active
													? classes.success
													: classes.error
											}
											size="small"
											label={
												item.active
													? "Active"
													: "Inactive"
											}
										/>
									</Grid>
									<Grid item lg={3} md={6} xs={12}>
										<Typography variant="body2" className={classes.label}>
											Attachments
										</Typography>
										{isNotNullOrEmpty(item.attachments) ? (
											<AttachmentListing attachments={item.attachments} />
										) : (
											"-"
										)}
									</Grid>
								</Grid>
								{!isStaffUser(role) && (
									<div className="ignore">
										<CrudOptions
											onDelete={() => onDelete(item.id)}
											onEdit={() => onEdit(item.id)}
										/>
									</div>
								)}
							</CardContent>
						</Card>
						);
					})
					:
					<Card
						data-cy={`equipment-empty`}
						className={clsx(classes.card, undefined)}
					>
						<CardContent className={classes.content}>
							<Grid
								container
								spacing={2}
								className={clsx(
									classes.container,
									classes.clickable
								)}
							>
								<Grid item lg={3} md={6} xs={12}>
									<Typography
										className={classes.label}
										variant="body2"
									>
										{ isLoading ? "Loading..." : "No equipment to show" }
									</Typography>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
			}
		</>
	);
};

EquipmentListing.propTypes = {
	equipment: PropTypes.array,
	isLoading: PropTypes.bool,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func
};

export default EquipmentListing;

